import React, { useState, useEffect } from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { Grid, Select, MenuItem, InputLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Checkbox } from "@mui/material";
import { get, postFile, postNoResponse } from "utils/WebService";
import moment from "moment";
import { MySwal } from "App";

function GerarRemessa() {
    const [contas, setContas] = useState([]);
    const [selectedConta, setSelectedConta] = useState("");
    const [selectedSituacao, setSelectedSituacao] = useState(1);
    const [boletos, setBoletos] = useState([]);
    const [selectedBoletos, setSelectedBoletos] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        carregarContas();
    }, []);

    const carregarContas = () => {
        get('/conta-bancaria/list-all').then(res => {
            if (res && res.length > 0) {
                setContas(res);
            }
        }).catch(err => console.error(err));
    };

    const handleContaChange = (e) => {
        const idConta = e.target.value;
        setSelectedConta(idConta);
        if (idConta) {
            buscarBoletos(idConta, selectedSituacao);
        } else {
            setBoletos([]);
        }
    };

    const handleSituacaoChange = (e) => {
        const situacao = e.target.value;
        setSelectedSituacao(situacao);
        if (selectedConta) {
            buscarBoletos(selectedConta, situacao);
        }
    };

    const handleRefresh = () => {
        if (selectedConta) {
            buscarBoletos(selectedConta, selectedSituacao);
        }
    };

    const buscarBoletos = (idConta, situacao) => {
        setLoading(true);
        const situacaoParam = situacao ? `&situacao=${situacao}` : '';
        get(`/cnab/boletos-pendentes?contaBancariaId=${idConta}${situacaoParam}`).then(res => {
            setBoletos(res);
            setSelectedBoletos([]); // Limpa seleção ao buscar novos
        }).catch(err => {
            console.error(err);
            MySwal.fire('Erro', 'Erro ao buscar boletos', 'error');
        }).finally(() => {
            setLoading(false);
        });
    };

    const handleGerarRemessa = () => {
        if (selectedBoletos.length === 0) {
            MySwal.fire('Aviso', 'Selecione pelo menos um boleto.', 'warning');
            return;
        }

        setLoading(true);
        postFile('/cnab/gerar-remessa', selectedBoletos, `CB${moment().format('DDMMYY')}.rem`)
            .then(() => {
                MySwal.fire('Sucesso', 'Remessa gerada com sucesso!', 'success');
                buscarBoletos(selectedConta);
            })
            .catch(err => {
                console.error(err);
                MySwal.fire('Erro', 'Erro ao gerar remessa.', 'error');
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleCancelarBoleto = (idParcela) => {
        MySwal.fire({
            title: 'Cancelar Boleto',
            text: "Deseja realmente cancelar este boleto? Como ele não foi enviado na remessa, será cancelado sumariamente.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sim, cancelar',
            cancelButtonText: 'Não'
        }).then((result) => {
            if (result.isConfirmed) {
                setLoading(true);
                postNoResponse("/acordo/parcela/boleto/cancelar", { idParcela })
                    .then(() => {
                        MySwal.fire('Sucesso', 'Boleto cancelado com sucesso!', 'success');
                        buscarBoletos(selectedConta, selectedSituacao);
                    })
                    .catch(err => {
                        console.error(err);
                        MySwal.fire('Erro', err.message || 'Erro ao cancelar boleto.', 'error');
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            }
        });
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelecteds = boletos.map((n) => n.idBoleto);
            setSelectedBoletos(newSelecteds);
            return;
        }
        setSelectedBoletos([]);
    };

    const handleClick = (event, id) => {
        const selectedIndex = selectedBoletos.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selectedBoletos, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selectedBoletos.slice(1));
        } else if (selectedIndex === selectedBoletos.length - 1) {
            newSelected = newSelected.concat(selectedBoletos.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selectedBoletos.slice(0, selectedIndex),
                selectedBoletos.slice(selectedIndex + 1),
            );
        }
        setSelectedBoletos(newSelected);
    };

    const isSelected = (id) => selectedBoletos.indexOf(id) !== -1;

    return (
        <MDBox>
            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Conta Bancária</InputLabel>
                    <Select
                        id="conta-select"
                        value={selectedConta}
                        onChange={handleContaChange}
                        size="small"
                        fullWidth
                        displayEmpty
                        sx={{ height: "30px" }}
                    >
                        <MenuItem value="" disabled><em>Selecione...</em></MenuItem>
                        {contas.map(conta => (
                            <MenuItem key={conta.id} value={conta.id}>
                                {conta.banco} - Ag: {conta.agencia} C/C: {conta.conta} ({conta.descricao})
                            </MenuItem>
                        ))}
                    </Select>
                </Grid>
                
                <Grid item xs={12} md={4}>
                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Situação do Boleto</InputLabel>
                    <Select
                        id="situacao-select"
                        value={selectedSituacao}
                        onChange={handleSituacaoChange}
                        size="small"
                        fullWidth
                        sx={{ height: "30px" }}
                    >
                        <MenuItem value={1}>Aguardando Envio (Emitidos)</MenuItem>
                        <MenuItem value={6}>Aguardando Cancelamento / Baixa</MenuItem>
                        <MenuItem value={2}>Enviados para Registro</MenuItem>
                        <MenuItem value={4}>Rejeitados</MenuItem>
                        <MenuItem value={7}>Cancelados</MenuItem>
                    </Select>
                </Grid>

                <Grid item xs={12} md={4} display="flex" alignItems="flex-end" gap={2}>
                    <MDButton 
                        variant="gradient" 
                        color="info" 
                        onClick={handleGerarRemessa}
                        sx={{ height: "30px" }}
                        disabled={selectedBoletos.length === 0 || loading || (selectedSituacao !== 1 && selectedSituacao !== 6)}
                        title={selectedSituacao !== 1 && selectedSituacao !== 6 ? "Só é possível gerar remessa para boletos Aguardando Envio ou Baixa" : ""}
                    >
                        GERAR REMESSA ({selectedBoletos.length})
                    </MDButton>

                    <MDButton 
                        variant="outlined" 
                        color="dark" 
                        onClick={handleRefresh}
                        sx={{ height: "30px" }}
                        disabled={loading || !selectedConta}
                        title="Atualizar lista"
                    >
                        ATUALIZAR
                    </MDButton>
                </Grid>

                <Grid item xs={12}>
                    <MDBox width="100%">
                        <TableContainer>
                            <Table sx={{ minWidth: 650 }} size="small" aria-label="boletos table">
                                <TableHead sx={{ display: "table-header-group" }}>
                                    <TableRow>
                                        {(selectedSituacao === 1 || selectedSituacao === 6) && (
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    color="primary"
                                                    indeterminate={selectedBoletos.length > 0 && selectedBoletos.length < boletos.length}
                                                    checked={boletos.length > 0 && selectedBoletos.length === boletos.length}
                                                    onChange={handleSelectAllClick}
                                                />
                                            </TableCell>
                                        )}
                                        <TableCell><MDTypography variant="caption" color="secondary" fontWeight="medium" textTransform="uppercase">ID</MDTypography></TableCell>
                                        <TableCell><MDTypography variant="caption" color="secondary" fontWeight="medium" textTransform="uppercase">Nosso Número</MDTypography></TableCell>
                                        <TableCell><MDTypography variant="caption" color="secondary" fontWeight="medium" textTransform="uppercase">Devedor</MDTypography></TableCell>
                                        <TableCell><MDTypography variant="caption" color="secondary" fontWeight="medium" textTransform="uppercase">CPF/CNPJ</MDTypography></TableCell>
                                        <TableCell><MDTypography variant="caption" color="secondary" fontWeight="medium" textTransform="uppercase">Vencimento</MDTypography></TableCell>
                                        <TableCell align="right"><MDTypography variant="caption" color="secondary" fontWeight="medium" textTransform="uppercase">Valor</MDTypography></TableCell>
                                        <TableCell align="center"><MDTypography variant="caption" color="secondary" fontWeight="medium" textTransform="uppercase">Ações</MDTypography></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {boletos.map((row) => {
                                        const isItemSelected = isSelected(row.idBoleto);
                                        return (
                                            <TableRow
                                                hover
                                                onClick={(event) => (selectedSituacao === 1 || selectedSituacao === 6) ? handleClick(event, row.idBoleto) : null}
                                                role="checkbox"
                                                aria-checked={isItemSelected}
                                                tabIndex={-1}
                                                key={row.idBoleto}
                                                selected={isItemSelected}
                                                sx={{ cursor: (selectedSituacao === 1 || selectedSituacao === 6) ? 'pointer' : 'default' }}
                                            >
                                                {(selectedSituacao === 1 || selectedSituacao === 6) && (
                                                    <TableCell padding="checkbox">
                                                        <Checkbox color="primary" checked={isItemSelected} />
                                                    </TableCell>
                                                )}
                                                <TableCell><MDTypography variant="button" fontWeight="regular" color="text">{row.idBoleto}</MDTypography></TableCell>
                                                <TableCell><MDTypography variant="button" fontWeight="regular" color="text">{row.nossoNumero}</MDTypography></TableCell>
                                                <TableCell><MDTypography variant="button" fontWeight="regular" color="text">{row.nomeDevedor}</MDTypography></TableCell>
                                                <TableCell><MDTypography variant="button" fontWeight="regular" color="text">{row.cpfCnpjDevedor}</MDTypography></TableCell>
                                                <TableCell><MDTypography variant="button" fontWeight="regular" color="text">{row.dataVencimento ? moment(row.dataVencimento).format("DD/MM/YYYY") : ""}</MDTypography></TableCell>
                                                <TableCell align="right"><MDTypography variant="button" fontWeight="regular" color="text">{row.valor ? `R$ ${row.valor.toFixed(2)}` : ""}</MDTypography></TableCell>
                                                <TableCell align="center">
                                                    {(selectedSituacao === 1 || selectedSituacao === 2 || selectedSituacao === 3) && (
                                                        <MDButton 
                                                            variant="text" 
                                                            color="error" 
                                                            size="small" 
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleCancelarBoleto(row.idParcela);
                                                            }}
                                                        >
                                                            Cancelar
                                                        </MDButton>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {boletos.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center">
                                                Nenhum boleto pendente para a conta selecionada.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </MDBox>
                </Grid>
            </Grid>
        </MDBox>
    );
}

export default GerarRemessa;
